import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

export interface DsGlobalOverlayAttachment {
  bringToFront(): void;
  detach(): void;
}

export interface DsGlobalOverlayOptions {
  /**
   * Bridges local DDS custom-property overrides from this element before the
   * overlay leaves its DOM tree. Global values keep inheriting from body so
   * live theme changes continue to work.
   */
  inheritCustomPropertiesFrom?: HTMLElement;
}

/**
 * Moves declarative overlays to the document root so microfrontend layout and
 * stacking contexts cannot constrain them. The original DOM position is kept
 * as an anchor so Angular can destroy the component normally.
 */
@Injectable({ providedIn: 'root' })
export class DsGlobalOverlayHostService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  attach(
    element: HTMLElement,
    options: DsGlobalOverlayOptions = {},
  ): DsGlobalOverlayAttachment | null {
    if (!isPlatformBrowser(this.platformId) || !this.document.body) {
      return null;
    }

    const restoreCustomProperties = options.inheritCustomPropertiesFrom
      ? this.copyCustomProperties(options.inheritCustomPropertiesFrom, element)
      : () => undefined;
    const origin = element.parentNode;
    if (!origin || origin === this.document.body) {
      return {
        bringToFront: () => this.document.body.appendChild(element),
        detach: restoreCustomProperties,
      };
    }

    const anchor = this.document.createComment('ds-global-overlay-origin');
    origin.insertBefore(anchor, element);
    this.document.body.appendChild(element);

    let attached = true;

    return {
      bringToFront: () => {
        if (attached) {
          this.document.body.appendChild(element);
        }
      },
      detach: () => {
        if (!attached) return;
        attached = false;

        if (anchor.parentNode) {
          anchor.parentNode.insertBefore(element, anchor);
          anchor.parentNode.removeChild(anchor);
        } else if (element.parentNode === this.document.body) {
          this.document.body.removeChild(element);
        }
        restoreCustomProperties();
      },
    };
  }

  private copyCustomProperties(source: HTMLElement, target: HTMLElement): () => void {
    const computed = getComputedStyle(source);
    const documentDefaults = getComputedStyle(this.document.body);
    const previous = new Map<string, { value: string; priority: string }>();

    for (let index = 0; index < computed.length; index += 1) {
      const property = computed.item(index);
      if (!property.startsWith('--ds-')) continue;

      // Inline properties already travel with the element. Global token values
      // continue to inherit from body, which keeps live theme changes working.
      // Only local inherited overrides need to be bridged across the portal.
      if (target.style.getPropertyValue(property)) continue;
      const value = computed.getPropertyValue(property);
      if (value === documentDefaults.getPropertyValue(property)) continue;

      previous.set(property, {
        value: target.style.getPropertyValue(property),
        priority: target.style.getPropertyPriority(property),
      });
      target.style.setProperty(property, value);
    }

    return () => {
      previous.forEach(({ value, priority }, property) => {
        if (value) {
          target.style.setProperty(property, value, priority);
        } else {
          target.style.removeProperty(property);
        }
      });
    };
  }
}
